import { supportsViewTransitions } from '@theme/utilities';

// Create a new custom element for product links with images for transitions to PDP
class ProductCardLink extends HTMLElement {
  /** @type {string | null} */
  #originalSrcset = null;

  /** @type {HTMLImageElement | null} */
  #modifiedImage = null;

  /** @type {Animation | null} */
  #pendingAnimation = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for the product link
   * @param {Event} event
   */
  #handleClick = (event) => {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    // Only prepare the transition when the click is actually on a navigating link
    const targetLink = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!targetLink) return;

    const gallery = this.querySelector('[data-view-transition-to-main-product]');
    if (!this.productTransitionEnabled || !(gallery instanceof HTMLElement)) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const activeImage =
      gallery.querySelector('slideshow-slide[aria-hidden="false"] [transitionToProduct="true"]') ||
      gallery.querySelector('[transitionToProduct="true"]:last-child');

    if (activeImage instanceof HTMLImageElement) {
      // iOS Safari is excluded from view transitions theme-wide, so the swap buys
      // nothing there. Worse, it freezes the page for the bfcache mid-animation:
      // the `finish` callback then runs *after* the back navigation has already
      // restored the srcset, leaving the featured media (often the FLAT image)
      // stuck on the card.
      if (supportsViewTransitions()) {
        this.#originalSrcset = activeImage.getAttribute('srcset');
        this.#modifiedImage = activeImage;
        this.#setImageSrcset(activeImage);
      }
    }

    gallery.setAttribute('data-view-transition-type', 'product-image-transition');
    gallery.setAttribute('data-view-transition-triggered', 'true');
  };

  /**
   * Restores the original image srcset when the page is restored from bfcache.
   * Without this, the srcset modified for the view transition persists and
   * can show the wrong image (e.g. a FLAT image instead of the featured media).
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    if (this.#pendingAnimation) {
      this.#pendingAnimation.cancel();
      this.#pendingAnimation = null;
    }

    if (this.#modifiedImage && this.#originalSrcset != null) {
      this.#modifiedImage.setAttribute('srcset', this.#originalSrcset);
      this.#modifiedImage = null;
      this.#originalSrcset = null;
    }

    const gallery = this.querySelector('[data-view-transition-triggered]');
    if (gallery) {
      gallery.removeAttribute('data-view-transition-triggered');
      gallery.removeAttribute('data-view-transition-type');
    }
  };

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      this.#pendingAnimation = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      this.#pendingAnimation.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
        this.#pendingAnimation = null;
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}
